export type BookingDetailOrigin =
  | 'enquiries'
  | 'bookings'
  | 'cancelled'
  | 'payments'
  | 'history';

export interface BookingNavigationState {
  from?: BookingDetailOrigin;
  returnPath?: string;
  // Full query string (page/limit/tab/search/...) of the list page at the
  // moment of navigating away, so "back" restores the exact same view.
  // NOTE: a tab value can legitimately be an empty string (e.g. "All"
  // tabs), so this is carried verbatim rather than as named fields that
  // would get dropped by a truthy check.
  returnSearch?: string;
}

const ORIGIN_RETURN_PATH: Record<BookingDetailOrigin, string> = {
  enquiries: '/enquires',
  bookings: '/bookings',
  cancelled: '/cancelled',
  payments: '/payments',
  history: '/enquires',
};

const isBookingDetailPath = (pathname: string) =>
  pathname.startsWith('/bookings/');

export const buildBookingDetailState = (
  origin: BookingDetailOrigin,
  options?: {
    returnPath?: string;
    // Pass the list page's current `location.search` here so "back" can
    // restore the exact page/limit/tab/search it was showing.
    returnSearch?: string;
  },
): BookingNavigationState => ({
  from: origin,
  returnPath: options?.returnPath ?? ORIGIN_RETURN_PATH[origin],
  returnSearch: options?.returnSearch,
});

// Detail pages that live outside their menu section's URL prefix — map them
// back to the menu path so the sidebar keeps the right tab highlighted.
const MENU_PATH_ALIASES: Array<[pathPrefix: string, menuPath: string]> = [
  ['/amc/', '/request/amc'],
  ['/copperpiping/', '/request/copperpiping'],
  ['/free-consultancy/', '/request/free-consultancy'],
];

export const getActiveMenuPath = (
  pathname: string,
  state: unknown,
): string => {
  const navState = state as BookingNavigationState | null | undefined;

  if (navState?.returnPath && isBookingDetailPath(pathname)) {
    return navState.returnPath;
  }

  const alias = MENU_PATH_ALIASES.find(([pathPrefix]) =>
    pathname.startsWith(pathPrefix),
  );
  if (alias) {
    return alias[1];
  }

  return pathname;
};

export const getAssignBookingHeaderTitle = (state: unknown): string => {
  const navState = state as BookingNavigationState | null | undefined;
  if (navState?.from === 'payments') {
    return 'Payment Details';
  }
  return 'Assign Technician';
};

export const getBookingBackTarget = (
  state: unknown,
): { path: string; replace: boolean } | null => {
  const navState = state as BookingNavigationState | null | undefined;
  if (!navState?.returnPath) {
    return null;
  }

  const query = navState.returnSearch?.replace(/^\?/, '');
  return {
    path: query ? `${navState.returnPath}?${query}` : navState.returnPath,
    replace: false,
  };
};
